import { useQueryClient } from '@tanstack/react-query';
import { isUndefined } from 'lodash';
import { useCallback } from 'react';

import { type DiscussionLocation, useTrackAddedComment } from '@air/analytics';
import { type Account, type Annotation } from '@air/api/types';
import { useToasts } from '@air/provider-toast';

import { getAnnotationAnalyticsType, hasAnnotation } from '~/components/Annotations/shared/annotationUtils';
import { NEW_DISCUSSION_ID } from '~/components/AnnotationsV2/shared/useActiveDiscussionIdObserver';
import { useUpdateStateAfterAddingOrRemovingDiscussion } from '~/components/DiscussionsV2/utils';
import { getDiscussionsKey } from '~/constants/react-query-keys';
import { QueryParamNames } from '~/constants/search';
import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { useAnnotationContext } from '~/providers/AnnotationProvider/AnnotationProvider';
import { centralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { centralizedClipSelector, centralizedClipTypeSelector } from '~/store/centralizedClip/selectors';
import { activeDiscussionIdParamSelector } from '~/store/router/selectors';
import { getAccountKey } from '~/swr-hooks/account/useAccount';
import { getCommentTime } from '~/swr-hooks/discussions/getCommentTime';
import { useCreateDiscussion } from '~/swr-hooks/discussions/useCreateDiscussion';
import { setQueryParams } from '~/utils/PathUtils';
import { useAirStore } from '~/utils/ReduxUtils';

export type SubmitNewDiscussionParams = {
  newComment: string;
  timestamp?: number;
  endTimestamp?: number;
  trackLocation: DiscussionLocation;
  annotations?: Annotation[];
};

/**
 * `shortId` picks the API family the discussion is created through: a share link's shortId routes
 * to the public share-link endpoint, `undefined` routes to the private app's authenticated
 * endpoint. Components never call this hook directly — the area-owned twins
 * (`usePublicSubmitNewDiscussion` / `usePrivateSubmitNewDiscussion`) bind the source.
 */
export const useSubmitNewDiscussion = ({ shortId }: { shortId: string | undefined }) => {
  const { setActiveAnnotation } = useAnnotationContext();
  const store = useAirStore();
  const { mutateAsync: createDiscussion } = useCreateDiscussion();
  const { trackAddedComment } = useTrackAddedComment();
  const queryClient = useQueryClient();

  const { showToast } = useToasts();
  const { isSearchActive } = useIsSearchActive();

  const { updateStateAfterAddingOrRemovingDiscussion } = useUpdateStateAfterAddingOrRemovingDiscussion();

  const submitNewDiscussion = useCallback(
    async ({ newComment, timestamp, endTimestamp, trackLocation, annotations }: SubmitNewDiscussionParams) => {
      const account = queryClient.getQueryData<Account>(getAccountKey());
      const centralizedBoard = centralizedBoardSelector(store.getState());
      const clip = centralizedClipSelector(store.getState());

      // Find the correct board for the discussion:
      // - For search results, use undefined (no specific board context)
      // - For share links (empty boards array), use centralized board
      // - Otherwise find matching board from clip.boards or fall back to centralized board
      let board: { id?: string; title?: string } | undefined = centralizedBoard;

      if (isSearchActive) {
        board = undefined;
      } else if (clip.boards?.length) {
        const clipBoardMatchingCentralized = clip.boards.find((b) => b.id === centralizedBoard?.id);
        const clipBoardDescendingFromCentralized = clip.boards.find((b) =>
          b.ancestors?.some((ancestor) => ancestor.id === centralizedBoard?.id),
        );
        board = clipBoardMatchingCentralized ?? clipBoardDescendingFromCentralized ?? centralizedBoard;
      }

      if (!account) {
        return;
      }

      const analyticsAnnotation = annotations?.[0];

      trackAddedComment({
        boardId: board?.id,
        clipId: clip.id,
        comment: newComment,
        comment_time: getCommentTime({ timestamp, endTimestamp }),
        location: trackLocation,
        startedDiscussion: true,
        has_timestamp: !isUndefined(timestamp),
        has_annotation: !!annotations?.length,
        public: trackLocation === 'public-board',
        annotationType: getAnnotationAnalyticsType(analyticsAnnotation),
        assetType: centralizedClipTypeSelector(store.getState()),
      });

      updateStateAfterAddingOrRemovingDiscussion({
        newCommentCount: clip.openCommentCount ? clip.openCommentCount + 1 : 1,
        newDiscussionCount: clip.openDiscussionCount ? clip.openDiscussionCount + 1 : 1,
        clipId: clip.id,
      });

      try {
        const createdDiscussion = await createDiscussion({
          assetId: clip.assetId,
          account,
          board: { id: board?.id, title: board?.title },
          clipId: clip.id,
          key: getDiscussionsKey({
            assetId: clip.assetId,
            boardId: board?.id,
            shortId,
            // for public boards we put clipId in request
            clipId: shortId ? clip.id : undefined,
          }),
          newComment,
          shortId,
          timestamp,
          endTimestamp,
          type: 'clip',
          currentVersion: clip.version,
          annotations,
          beforeCacheUpdate: async (discussion) => {
            if (hasAnnotation(discussion)) {
              await setQueryParams({ [QueryParamNames.discussionId]: NEW_DISCUSSION_ID }, 'replace');
              setActiveAnnotation(discussion.annotation);
            }
          },
        });

        if (createdDiscussion) {
          const activeDiscussionId = activeDiscussionIdParamSelector(store.getState());

          // we have to check if activeDiscussionId is NEW_DISCUSSION_ID, because user can click another comment before this request is finished
          // or video may be played
          if (
            activeDiscussionId === NEW_DISCUSSION_ID &&
            'annotation' in createdDiscussion &&
            hasAnnotation(createdDiscussion)
          ) {
            setQueryParams({ [QueryParamNames.discussionId]: createdDiscussion.id }, 'replace');
          }
        }
      } catch (_error) {
        showToast('Failed to create discussion', { color: 'red' });
      }
    },
    [
      queryClient,
      store,
      trackAddedComment,
      updateStateAfterAddingOrRemovingDiscussion,
      createDiscussion,
      shortId,
      setActiveAnnotation,
      showToast,
      isSearchActive,
    ],
  );

  return {
    submitNewDiscussion,
  };
};
